import { useEffect, useState } from "react";
import { Download, UtensilsCrossed, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const DIA_HOY = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"][new Date().getDay()];

export default function MenuEscolar() {
  const [menu, setMenu] = useState(null);
  const [paginas, setPaginas] = useState([]);

  useEffect(() => {
    let urls = [];
    api.get("/menu-escolar").then(async ({ data }) => {
      setMenu(data);
      for (let i = 0; i < data.paginas; i += 1) {
        const r = await api.get(`/menu-escolar/pagina/${i}`, { responseType: "blob" });
        urls = [...urls, URL.createObjectURL(r.data)];
        setPaginas(urls);
      }
    }).catch(() => setMenu({ semanas: [], paginas: 0 }));
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const descargar = async () => {
    const r = await api.get("/menu-escolar/archivo", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = menu.nombre_archivo || "menu-escolar.pdf";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  if (!menu) return <Loading />;

  const semanas = menu.semanas || [];
  const vacio = semanas.length === 0 && !menu.tiene_archivo;
  const ordenadas = semanas
    .map((semana, indice) => ({ semana, indice }))
    .sort((a, b) => (b.indice === menu.semana_activa) - (a.indice === menu.semana_activa));

  return (
    <div data-testid="portal-menu">
      <PageTitle title={menu.titulo || "Menú escolar"}
                 subtitle={`Lo que se sirve cada día en el comedor del colegio. Resaltamos los platos de ${DIA_HOY}.`}>
        {menu.tiene_archivo && (
          <Button variant="outline" className="rounded-full" onClick={descargar}
                  data-testid="descargar-menu">
            <Download className="mr-2 h-4 w-4" /> Descargar PDF
          </Button>
        )}
      </PageTitle>

      {vacio ? (
        <Empty testId="menu-vacio">El colegio todavía no ha publicado el menú escolar.</Empty>
      ) : (
        <>
          {semanas.length > 0 && (
            <div className="grid gap-6 lg:grid-cols-2" data-testid="menu-semanas">
              {ordenadas.map(({ semana: s, indice }) => {
                const activa = menu.semana_activa === indice;
                return (
                <article key={s.nombre + indice}
                         className={`lift rounded-lg border bg-card p-6 sm:p-8 ${
                           activa ? "border-primary ring-1 ring-primary/40" : "border-border/60"}`}
                         data-testid={activa ? "semana-actual" : undefined}>
                  <div className="flex flex-wrap items-center gap-3 text-primary">
                    <CalendarDays className="h-4 w-4" />
                    <h2 className="font-display text-lg font-bold">{s.nombre}</h2>
                    {activa && <Badge className="text-[10px]">esta semana</Badge>}
                  </div>
                  <div className="mt-5 space-y-4">
                    {(s.dias || []).map((d) => {
                      const esHoy = d.dia === DIA_HOY;
                      return (
                        <div key={d.dia}
                             className={`rounded-md border p-4 ${esHoy
                               ? "border-primary/50 bg-primary/5" : "border-border/50 bg-background"}`}>
                          <div className="flex items-center gap-2">
                            <p className={`text-xs font-bold uppercase tracking-[0.18em] ${
                              esHoy ? "text-primary" : "text-muted-foreground"}`}>
                              {d.dia}
                            </p>
                          </div>
                          <p className="mt-2 text-sm leading-relaxed">{d.platos || "—"}</p>
                        </div>
                      );
                    })}
                  </div>
                </article>
                );
              })}
            </div>
          )}

          {paginas.length > 0 && (
            <section className="mt-12">
              <div className="flex items-center gap-2 text-muted-foreground">
                <UtensilsCrossed className="h-4 w-4" />
                <h2 className="font-display text-lg font-semibold text-foreground">Menú original del colegio</h2>
              </div>
              <div className="mt-5 space-y-6" data-testid="menu-paginas">
                {paginas.map((src, i) => (
                  <img key={src} src={src} alt={`Menú escolar página ${i + 1}`}
                       data-testid={`menu-pagina-${i}`}
                       className="w-full rounded-lg border border-border/60 bg-white shadow-sm" />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
